"use client";

import { useSignIn, useSignUp } from "@clerk/nextjs";
import type { OAuthStrategy } from "@clerk/types";

type GoogleSignInButtonProps = {
  onBeforeRedirect?: () => void;
};

const GoogleSignInButton = ({ onBeforeRedirect }: GoogleSignInButtonProps) => {
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { signUp, setActive, isLoaded: signUpLoaded } = useSignUp();

  // Guard against taps before the Clerk SDK is ready — otherwise the click
  // handler silently does nothing and the button looks broken.
  const isLoaded = signInLoaded && signUpLoaded;

  const signInWith = (strategy: OAuthStrategy) => {
    return signIn?.authenticateWithRedirect({
      strategy,
      redirectUrl: "/sso-callback",
      redirectUrlComplete: "/onboarding/membership",
    });
  };

  const handleClick = async () => {
    if (!signIn || !signUp) return;

    // Handle transferable accounts (user exists with OAuth but not in app, or vice versa)
    const userExistsButNeedsToSignIn =
      signUp.verifications.externalAccount.status === "transferable" &&
      signUp.verifications.externalAccount.error?.code ===
        "external_account_exists";

    if (userExistsButNeedsToSignIn) {
      const res = await signIn.create({ transfer: true });
      if (res.status === "complete") {
        // Ziel an Clerk uebergeben statt danach zu navigieren — ein
        // router.push wird von Clerks eigenem Redirect ueberschrieben.
        // Siehe AuthCard, Sign-in-Zweig.
        await setActive({
          session: res.createdSessionId,
          redirectUrl: "/onboarding/membership",
        });
      }
      return;
    }

    const userNeedsToBeCreated =
      signIn.firstFactorVerification.status === "transferable";

    if (userNeedsToBeCreated) {
      const res = await signUp.create({ transfer: true });
      if (res.status === "complete") {
        await setActive({
          session: res.createdSessionId,
          redirectUrl: "/onboarding/membership?initial=true",
        });
      }
      return;
    }

    onBeforeRedirect?.();
    signInWith("oauth_google");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isLoaded}
      className="flex h-[46px] w-full items-center justify-center gap-2 rounded-full border border-gray-10 bg-white px-6 py-4 text-black transition-colors hover:bg-sand-10 disabled:opacity-50"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className="size-5"
        aria-hidden="true"
      >
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          className="fill-google-blue"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          className="fill-google-green"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          className="fill-google-yellow"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          className="fill-google-red"
        />
      </svg>
      Mit Google anmelden
    </button>
  );
};

export { GoogleSignInButton };
